/**
 * Vendor Card Component
 * Displays a linked vendor in a message thread - WhatsApp style card
 */

import React, { useState, useEffect } from 'react';
import { Building2, Calendar, MapPin, Mail, Phone, User, AlertCircle, ExternalLink } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Vendor } from '@/firebase/vendorService';
import { AgreementCompletion } from '@/firebase/agreementCompletionService';
import { locationService } from '@/firebase/locationService';

interface VendorCardProps {
  vendorId: string;
  organizationId: string;
  onViewVendor?: (vendor: Vendor) => void;
}

const VendorCard: React.FC<VendorCardProps> = ({ vendorId, organizationId, onViewVendor }) => {
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastContractExpiry, setLastContractExpiry] = useState<Date | null>(null);
  const [contractCount, setContractCount] = useState(0);
  const [locationNames, setLocationNames] = useState<string[]>([]);

  useEffect(() => {
    loadVendor();
  }, [vendorId, organizationId]);

  const loadVendor = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch vendor document
      const vendorRef = doc(db, 'vendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);

      if (!vendorSnap.exists()) {
        setError('Vendor not found');
        setLoading(false);
        return;
      }

      const vendorData = { id: vendorSnap.id, ...vendorSnap.data() } as Vendor;
      setVendor(vendorData);

      // Load locations
      if (vendorData.locationIds && vendorData.locationIds.length > 0) {
        const locationsResult = await locationService.getAllLocations();
        if (locationsResult.success && locationsResult.data) {
          const names = vendorData.locationIds
            .map(locId => {
              const location = locationsResult.data?.find(l => l.id === locId);
              return location?.name;
            })
            .filter((name): name is string => !!name);
          setLocationNames(names);
        }
      }

      // Load vendor contracts to get latest expiry date
      try {
        const { agreementCompletionService } = await import('@/firebase/agreementCompletionService');
        const contractsResult = await agreementCompletionService.getCompletionsForVendor(
          vendorId,
          organizationId,
          vendorData.name
        );

        if (contractsResult.success && contractsResult.data) {
          setContractCount(contractsResult.data.length);

          // Find the latest expiry date from all contracts
          let latestExpiry: Date | null = null;
          contractsResult.data.forEach((contract: AgreementCompletion) => {
            if (contract.expiryDate) {
              const expiryDate = convertTimestamp(contract.expiryDate);
              if (expiryDate && (!latestExpiry || expiryDate > latestExpiry)) {
                latestExpiry = expiryDate;
              }
            }
          });

          setLastContractExpiry(latestExpiry);
        }
      } catch (contractError) {
        console.error('Error loading vendor contracts:', contractError);
        // Don't fail the whole card if contracts fail to load
      }
    } catch (err: any) {
      console.error('Error loading vendor:', err);
      setError(err.message || 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const convertTimestamp = (ts: any): Date | null => {
    if (!ts) return null;
    if (ts?.toDate) return ts.toDate();
    if (ts?._seconds) return new Date(ts._seconds * 1000);
    if (ts instanceof Date) return ts;
    if (typeof ts === 'string') {
      const parsed = new Date(ts);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (typeof ts === 'number') return new Date(ts);
    return null;
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;

    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const getExpiryStatus = (expiryDate: Date | null): { color: string; icon: React.ReactNode; text: string } | null => {
    if (!expiryDate) return null;

    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <AlertCircle className="w-4 h-4" />,
        text: 'Expired',
      };
    } else if (diffDays <= 30) {
      return {
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <AlertCircle className="w-4 h-4" />,
        text: 'Expiring Soon',
      };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 my-2 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <p className="text-sm text-red-800">{error || 'Vendor not found'}</p>
      </div>
    );
  }

  const expiryStatus = getExpiryStatus(lastContractExpiry);

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-3 my-2 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onViewVendor && onViewVendor(vendor)}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <Building2 className="w-4 h-4 text-purple-600 flex-shrink-0" />
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {vendor.name}
          </h4>
        </div>
        {onViewVendor && (
          <ExternalLink className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-2" />
        )}
      </div>

      <div className="space-y-1.5">
        {/* Category */}
        {vendor.category && (
          <div className="text-xs text-gray-600">
            <span className="font-medium">Category:</span> {vendor.category}
          </div>
        )}

        {/* Contact Info */}
        {vendor.contact && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <User className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{vendor.contact}</span>
          </div>
        )}

        {/* Email */}
        {vendor.email && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{vendor.email}</span>
          </div>
        )}

        {/* Phone */}
        {vendor.phone && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <Phone className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{vendor.phone}</span>
          </div>
        )}

        {/* Locations */}
        {locationNames.length > 0 && (
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">
              {locationNames.length === 1 
                ? locationNames[0]
                : `${locationNames.length} location${locationNames.length > 1 ? 's' : ''}`
              }
            </span>
          </div>
        )}

        {/* Contract Info */}
        {lastContractExpiry && (
          <div className="flex items-center space-x-2 text-xs">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <div className="flex items-center space-x-2 flex-1">
              <span className={expiryStatus ? 'text-gray-700' : 'text-gray-600'}>
                Last contract expires: {formatDate(lastContractExpiry)}
              </span>
              {expiryStatus && (
                <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-xs font-medium border ${expiryStatus.color}`}>
                  {expiryStatus.icon}
                  <span>{expiryStatus.text}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Contract Count */}
        {contractCount > 0 && (
          <div className="text-xs text-gray-500 pt-1">
            {contractCount} contract{contractCount !== 1 ? 's' : ''} on file
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorCard;

